import { Logo, LogoMark } from './Logo';
import { Divider } from './ui';

interface FooterLink {
  label: string;
  href: string;
}

interface FooterColumn {
  title: string;
  links: FooterLink[];
}

const LIFTER_COLUMNS: FooterColumn[] = [
  {
    title: 'Product',
    links: [
      { label: 'The Grid', href: '#grid' },
      { label: 'AI Coach', href: '#coach' },
      { label: 'watchOS app', href: '#features' },
      { label: 'Pricing', href: '#pricing' },
      { label: 'For coaches & gyms', href: '#/for-coaches' },
    ],
  },
  {
    title: 'Platforms',
    links: [
      { label: 'Web', href: '#grid' },
      { label: 'iOS & Android', href: '#features' },
      { label: 'Apple Watch', href: '#features' },
      { label: 'Data export (CSV/JSON)', href: '#faq' },
    ],
  },
  {
    title: 'Company',
    links: [
      { label: 'Brand assets', href: '/brand/lift2fit-sticker.svg' },
      { label: 'FAQ', href: '#faq' },
      { label: 'Privacy Policy', href: '#/legal/privacy' },
      { label: 'Terms of Service', href: '#/legal/terms' },
      { label: 'Health disclaimer', href: '#/legal/health-disclaimer' },
      { label: 'Support', href: 'mailto:support@lift2fit.com' },
    ],
  },
];

/** The CRM page carries its own footer columns; the two audiences never share a nav. */
const CRM_COLUMNS: FooterColumn[] = [
  {
    title: 'For coaches & gyms',
    links: [
      { label: 'Who it’s for', href: '#crm-who' },
      { label: 'The product', href: '#crm-product' },
      { label: 'Your clients’ app', href: '#crm-clients' },
      { label: 'Compared with the competition', href: '#crm-compare' },
      { label: 'Pricing', href: '#crm-pricing' },
    ],
  },
  {
    title: 'Platform',
    links: [
      { label: 'AI assistance', href: '#crm-product' },
      { label: 'Calendar & bookings', href: '#crm-product' },
      { label: 'Payments & revenue', href: '#crm-product' },
      { label: 'Contact us', href: 'mailto:info@lift2fit.com' },
    ],
  },
  {
    title: 'Lift2Fit',
    links: [
      { label: 'Lifter app', href: '/' },
      { label: 'Brand assets', href: '/brand/lift2fit-sticker.svg' },
      { label: 'Privacy Policy', href: '#/legal/privacy' },
      { label: 'Terms of Service', href: '#/legal/terms' },
      { label: 'Health disclaimer', href: '#/legal/health-disclaimer' },
      { label: 'Support', href: 'mailto:support@lift2fit.com' },
    ],
  },
];

export function Footer({ audience = 'lifter' }: { audience?: 'lifter' | 'crm' }) {
  const columns = audience === 'crm' ? CRM_COLUMNS : LIFTER_COLUMNS;

  return (
    <footer className="border-t border-hairline bg-brand-charcoal text-brand-white">
      <div className="mx-auto max-w-[1200px] 2xl:max-w-[1440px] min-[1920px]:max-w-[1720px] px-4 py-14 sm:px-6">
        <div className="grid gap-10 lg:grid-cols-[1.4fr_repeat(3,1fr)]">
          <div className="flex flex-col gap-4">
            <Logo className="h-16 w-auto text-brand-white" />
            <p className="max-w-[38ch] text-[13px] leading-relaxed text-brand-white/70">
              Lift2Fit turns the spreadsheet you already keep into a coach that actually talks back. Track it. Beat it.
            </p>
            <p className="font-display text-[15px] font-black leading-snug tracking-[-0.01em] text-brand-white">
              One stop for all your fitness needs.
            </p>
            <div className="flex items-center gap-3 text-brand-white/60">
              <LogoMark className="h-9 w-auto" />
              <span className="text-[11px] font-bold uppercase tracking-[0.08em]">Est. 2026 · lift2fit.com</span>
            </div>
          </div>

          {columns.map((column) => (
            <div key={column.title} className="flex flex-col gap-3">
              <h3 className="text-[11px] font-bold uppercase tracking-[0.1em] text-accent">{column.title}</h3>
              <ul className="flex flex-col gap-2">
                {column.links.map((link) => (
                  <li key={link.label}>
                    <a
                      href={link.href}
                      className="text-[13px] text-brand-white/80 transition-colors duration-150 hover:text-accent"
                    >
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <Divider className="my-10 border-brand-white/15" />

        <div className="flex flex-col gap-4 text-[11px] leading-relaxed text-brand-white/55 lg:flex-row lg:items-center lg:justify-between">
          <p>© {new Date().getFullYear()} Lift2Fit. All rights reserved.</p>
          <p className="max-w-[62ch]">
            Lift2Fit is a training log, not a medical device. Nothing here is medical advice. Consult a qualified
            professional before starting a new training programme, especially with an existing injury.
          </p>
        </div>
      </div>
    </footer>
  );
}
